import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import PromoBanner from "@/components/PromoBanner";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";
import SignupPromoController from "@/components/SignupPromoController";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import { loadGoogleAds } from "@/lib/googleAds";
// Landing stays eager — it is the most common entry point and the LCP page.
import Landing from "@/pages/Landing";

// All other routes are code-split to reduce the initial JS payload.
const Browse = lazy(() => import("@/pages/Browse"));
const NoteDetail = lazy(() => import("@/pages/NoteDetail"));
const Upload = lazy(() => import("@/pages/Upload"));
const Sell = lazy(() => import("@/pages/Sell"));
const NotesHub = lazy(() => import("@/pages/NotesHub"));
const NotesSubject = lazy(() => import("@/pages/NotesSubject"));
const AITools = lazy(() => import("@/pages/AITools"));
const AITutor = lazy(() => import("@/pages/AITutor"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Auth = lazy(() => import("@/pages/Auth"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Profile = lazy(() => import("@/pages/Profile"));
const SellerDashboard = lazy(() => import("@/pages/SellerDashboard"));
const PlusPage = lazy(() => import("@/pages/PlusPage"));
const CourseCommunity = lazy(() => import("@/pages/CourseCommunity"));
const StudyGroupDetail = lazy(() => import("@/pages/StudyGroupDetail"));
const MyGroups = lazy(() => import("@/pages/MyGroups"));
const Messages = lazy(() => import("@/pages/Messages"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const DailyStudy = lazy(() => import("@/pages/DailyStudy"));
const MyPurchases = lazy(() => import("@/pages/MyPurchases"));
const Wishlist = lazy(() => import("@/pages/Wishlist"));
const AdminPayouts = lazy(() => import("@/pages/AdminPayouts"));
const AdminInquiries = lazy(() => import("@/pages/AdminInquiries"));
const AdminNotes = lazy(() => import("@/pages/AdminNotes"));
const AdminPromotions = lazy(() => import("@/pages/AdminPromotions"));
const AdminSeo = lazy(() => import("@/pages/AdminSeo"));
const AdminUsers = lazy(() => import("@/pages/AdminUsers"));
const AdminMarketplaceAnalytics = lazy(() => import("@/pages/AdminMarketplaceAnalytics"));
const HelpSupport = lazy(() => import("@/pages/HelpSupport"));
const SavedMaterials = lazy(() => import("@/pages/SavedMaterials"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const Challenge = lazy(() => import("@/pages/Challenge"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const NotesLevel = lazy(() => import("@/pages/NotesLevel"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const MyOffers = lazy(() => import("@/pages/MyOffers"));
const SellerOffers = lazy(() => import("@/pages/SellerOffers"));
const OAuthConsent = lazy(() => import("@/pages/OAuthConsent"));
const Connect = lazy(() => import("@/pages/Connect"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Cache data to cut repeat database reads when navigating between pages.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 1,
    },
  },
});

const App = () => {
  // Load the Google Ads tag for returning visitors who already accepted cookies.
  useEffect(() => {
    loadGoogleAds();
  }, []);

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AnalyticsTracker />
          <PromoBanner />
          <Navbar />
          <main id="main-content">
            <ChunkErrorBoundary>
              <Suspense fallback={<div className="min-h-[60vh]" aria-hidden="true" />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/browse" element={<Browse />} />
                  <Route path="/note/:id" element={<NoteDetail />} />
                  <Route path="/upload" element={<Upload />} />
                  <Route path="/sell" element={<Sell />} />
                  <Route path="/notes" element={<NotesHub />} />
                  <Route path="/notes/:level" element={<NotesLevel />} />
                  <Route path="/notes/:level/:subject" element={<NotesSubject />} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/ai-tools" element={<AITools />} />
                  <Route path="/ai-tutor" element={<AITutor />} />
                  <Route path="/leaderboard" element={<Leaderboard />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                  <Route path="/connect" element={<Connect />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/dashboard" element={<SellerDashboard />} />
                  <Route path="/plus" element={<PlusPage />} />
                  <Route path="/course/:level/:code" element={<CourseCommunity />} />
                  <Route path="/study-group/:groupId" element={<StudyGroupDetail />} />
                  <Route path="/my-groups" element={<MyGroups />} />
                  <Route path="/messages" element={<Messages />} />
                  <Route path="/user/:userId" element={<UserProfile />} />
                  <Route path="/daily-study" element={<DailyStudy />} />
                  <Route path="/my-purchases" element={<MyPurchases />} />
                  <Route path="/wishlist" element={<Wishlist />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/admin/payouts" element={<AdminPayouts />} />
                  <Route path="/admin/inquiries" element={<AdminInquiries />} />
                  <Route path="/admin/notes" element={<AdminNotes />} />
                  <Route path="/admin/promotions" element={<AdminPromotions />} />
                  <Route path="/admin/seo" element={<AdminSeo />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/analytics" element={<AdminMarketplaceAnalytics />} />
                  <Route path="/help" element={<HelpSupport />} />
                  <Route path="/saved-materials" element={<SavedMaterials />} />
                  <Route path="/referrals" element={<Referrals />} />
                  <Route path="/challenge" element={<Challenge />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/my-offers" element={<MyOffers />} />
                  <Route path="/seller/offers" element={<SellerOffers />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ChunkErrorBoundary>
          </main>
          <Footer />
          <CookieBanner />
          <SignupPromoController />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
